import React from "react"
import { ArrowRight, CalendarDays, MessageCircle, ShieldCheck, Sparkles, UsersRound } from "lucide-react"

const privacySections = [
  ["Information we collect", "Account names and email addresses; encrypted passwords; team, player, family contact, game, practice, attendance, availability, lineup, snack, and communication data; plus limited device, usage, and error information needed to operate and secure the service."],
  ["How we use information", "We use information to provide accounts, organize teams, generate fair lineups, deliver reminders, support users, improve reliability, prevent abuse, and meet legal obligations. We do not sell personal information."],
  ["Public team pages", "A coach may share a public team URL. It exposes the team identity and schedule information shown on that page. Player contact details and account credentials are never part of the public endpoint."],
  ["Service providers", "We may use infrastructure, storage, error-monitoring, payment, email, and communication providers solely to operate Baseball Hub. Each receives only the information needed for its role."],
  ["Security and retention", "Passwords are hashed and API sessions are revocable. Data is sent over HTTPS in production. We retain data while an account is active or as required for service and legal purposes; deleting an account removes its owned team data."],
  ["Your choices", "You can update account and team information, export roster data, disconnect integrations, or delete your account from Settings. Coaches are responsible for obtaining appropriate permission before entering player or family information."],
  ["Children and contact", "Baseball Hub is operated for adult coaches and administrators and is not directed to children under 13. Questions or requests can be sent to privacy@baseballhub.net."],
]

const termsSections = [
  ["Acceptance and eligibility", "By using Baseball Hub, you agree to these terms. You must be authorized to manage the teams and information you enter, and you must be at least 18 years old."],
  ["The service", "Baseball Hub provides roster, schedule, attendance, availability, lineup, snack, public-calendar, and team-communication tools. Features may change as the product improves."],
  ["Accounts", "You are responsible for accurate account information, safeguarding credentials, and activity under your account. Notify us if you suspect unauthorized access."],
  ["Your content", "You retain ownership of data you submit. You grant Baseball Hub a limited license to host, process, and display it only as needed to provide and protect the service."],
  ["Third-party services", "GroupMe, calendar clients, and other connected services have separate terms."],
  ["Acceptable use", "Do not break laws, violate another person’s rights, upload malicious material, probe or disrupt the service, bypass access controls, or use another coach’s team data without permission."],
  ["Availability and warranty", "The service is provided as available without warranties to the maximum extent allowed by law. Lineup suggestions are coaching aids; coaches remain responsible for safety, eligibility, and game decisions."],
  ["Liability and termination", "To the extent allowed by law, Baseball Hub is not liable for indirect or consequential losses. We may suspend access for misuse, and you may stop using the service and delete your account at any time."],
  ["Changes and contact", "Material changes will be posted with a revised date. Questions about these terms can be sent to support@baseballhub.net."],
]

function Landing() {
  const features = [
    [Sparkles, "Fair lineups without the spreadsheet", "Build balanced fielding assignments, protect pitcher and catcher eligibility, and make last-minute changes before first pitch.", "Lineups", true],
    [UsersRound, "Know who is ready", "Keep roster details, positions, availability, and practice attendance together for the whole season.", "Roster"],
    [CalendarDays, "One schedule everyone can follow", "Plan games and practices, publish a team page, and let families subscribe from their calendar app.", "Schedule"],
    [MessageCircle, "Keep families in the loop", "Connect GroupMe, invite roster contacts, send updates, and automate the reminders coaches usually have to chase.", "Updates"],
  ]
  const appStoreUrl = "https://apps.apple.com/us/app/baseball-hub/id6746953086"

  return (
    <main className="marketing-page">
      <header className="marketing-header">
        <div className="marketing-header__inner">
          <a className="marketing-brand" href="/landing" aria-label="Baseball Hub home">
            <img src="/brand/baseball-hub-logo.png" alt="" />
            <strong>Baseball Hub</strong>
          </a>
          <nav className="marketing-header__actions" aria-label="Primary">
            <a className="marketing-header__ios" href={appStoreUrl}>Download for iOS</a>
            <a className="button button--primary" href="/login">Open the web app <ArrowRight size={16} aria-hidden="true" /></a>
          </nav>
        </div>
      </header>

      <section className="marketing-hero">
        <div className="marketing-hero__inner">
          <div className="marketing-hero__copy">
            <p className="eyebrow eyebrow--light">Built for the people who keep game day moving</p>
            <h1>A calmer clubhouse from first practice to final inning.</h1>
            <p>Rosters, schedules, fair lineups, attendance, and family updates—organized in one place so you can spend more time coaching.</p>
            <div className="marketing-hero__actions">
              <a className="button button--primary marketing-button--light" href="/login">Open the web app <ArrowRight size={17} aria-hidden="true" /></a>
              <a className="button marketing-button--ghost" href={appStoreUrl}>Download for iOS</a>
            </div>
            <p className="marketing-hero__note"><ShieldCheck size={16} aria-hidden="true" /> Your private team details stay behind your account.</p>
          </div>
          <figure className="marketing-hero__media">
            <img src="/brand/youth-baseball-sunset.jpg" alt="Youth baseball player ready in the field during a sunset game" />
            <figcaption><span>One plan</span> for coaches, players, and families.</figcaption>
          </figure>
        </div>
      </section>

      <section className="marketing-benefits" aria-label="Baseball Hub benefits">
        <div className="marketing-benefits__inner">
          <p><strong>Rosters</strong><span>Every detail in reach</span></p>
          <p><strong>Schedules</strong><span>One source of truth</span></p>
          <p><strong>Lineups</strong><span>Fair playing time</span></p>
          <p><strong>Updates</strong><span>Families kept ready</span></p>
        </div>
      </section>

      <section className="marketing-story" aria-labelledby="marketing-story-title">
        <header className="marketing-section-heading">
          <p className="eyebrow">Everything your season needs</p>
          <h2 id="marketing-story-title">Less coordinating. More coaching.</h2>
          <p>Baseball Hub keeps the details connected from the first roster invite through the last inning.</p>
        </header>
        <div className="marketing-feature-grid">
          {features.map(([Icon, title, copy, label, lead]) => (
            <article className={`marketing-feature${lead ? " marketing-feature--lead" : ""}`} key={title}>
              {lead && (
                <div className="marketing-lineup-preview" aria-hidden="true">
                  <div className="marketing-lineup-preview__header">
                    <div><span>Fielding plan</span><strong>Thunder vs. Hawks</strong></div>
                    <p><span /> Inning 4 of 6</p>
                  </div>
                  <div className="marketing-lineup-preview__labels"><span>Player</span><span>Last inning</span><span>Now</span></div>
                  <div className="marketing-lineup-preview__row"><strong><i>JM</i> Jordan M.</strong><span>2B</span><b>SS</b></div>
                  <div className="marketing-lineup-preview__row"><strong><i>CR</i> Casey R.</strong><span>RF</span><b>P</b></div>
                  <div className="marketing-lineup-preview__row"><strong><i>AT</i> Avery T.</strong><span>1B</span><b>C</b></div>
                  <div className="marketing-lineup-preview__row"><strong><i>LS</i> Logan S.</strong><span>LF</span><b>2B</b></div>
                  <div className="marketing-lineup-preview__row"><strong><i>MP</i> Morgan P.</strong><span>C</span><b>1B</b></div>
                  <div className="marketing-lineup-preview__footer"><span><i /> Playing time balanced</span><strong>All positions covered</strong></div>
                </div>
              )}
              <div className="marketing-feature__body">
                <div className="marketing-feature__icon"><Icon size={24} aria-hidden="true" /></div>
                <p className="marketing-feature__label">{label}</p>
                <h3>{title}</h3>
                <p>{copy}</p>
              </div>
            </article>
          ))}
        </div>
        <div className="marketing-privacy-note">
          <ShieldCheck size={22} aria-hidden="true" />
          <div><strong>Private by default</strong><p>Account and contact details stay protected, while coaches choose what appears on a shared team schedule.</p></div>
        </div>
      </section>

      <section className="marketing-final-cta">
        <div>
          <p className="eyebrow eyebrow--light">Your next season starts here</p>
          <h2>Put the whole team on the same page.</h2>
        </div>
        <a className="button marketing-button--light" href="/login">Open the web app <ArrowRight size={17} aria-hidden="true" /></a>
      </section>

      <footer className="marketing-footer">
        <a className="marketing-brand" href="/landing" aria-label="Baseball Hub home">
          <img src="/brand/baseball-hub-logo.png" alt="" />
          <strong>Baseball Hub</strong>
        </a>
        <nav aria-label="Footer">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href={appStoreUrl}>iOS app</a>
          <a href="mailto:support@baseballhub.net">Support</a>
        </nav>
        <span>© {new Date().getFullYear()} Baseball Hub</span>
      </footer>
    </main>
  )
}

function LegalPage({ title, updated, intro, sections }) {
  return <main className="legal-page"><header><a className="back-link" href="/landing">← Baseball Hub</a><p className="eyebrow">Legal</p><h1>{title}</h1><p className="muted">Last updated: {updated}</p><p className="legal-intro">{intro}</p></header>{sections.map(([heading, copy], index) => <section key={heading}><h2>{index + 1}. {heading}</h2><p>{copy}</p></section>)}<footer className="legal-footer"><a href="/landing">Home</a><a href="/privacy">Privacy</a><a href="/terms">Terms</a></footer></main>
}

export function StaticPage({ page }) {
  if (page === "privacy") return <LegalPage title="Privacy Policy" updated="July 31, 2026" intro="This policy explains what Baseball Hub stores, why it is used, and the choices available to coaches and team administrators." sections={privacySections} />
  if (page === "terms") return <LegalPage title="Terms of Service" updated="July 31, 2026" intro="These terms govern use of Baseball Hub’s web application, mobile application, APIs, and related services." sections={termsSections} />
  return <Landing />
}
